import { ResizeMode } from '@wix/thumbnail-generator';
import { useExperiments } from '@wix/wix-experiments-react';
import classnames from 'classnames';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import { TOOLTIP_SHOW_DELAY } from '../../../../constants/Delay';
import { DragType } from '../../../../constants/DragType';
import { ExperimentSpec } from '../../../../constants/ExperimentSpec';
import { ThumbnailBackground } from '../../../../constants/ThumbnailBackground';
import { shouldSmartGridImageBeContained } from '../../../../containers/FilesManager/SmartGrid/utils';
import { RouteBuilder } from '../../../../utils/RouteHelpers';
import { makeCreatorLink } from '../../../../utils/creatorLink';
import {
  UnsplashClickOrigin,
  UnsplashTarget,
} from '../../../../utils/hoc/bi/withTrackUnsplashClick/withTrackUnsplashClick.types';
import { ActionClickOrigin } from '../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import { useTrackPreviewActions } from '../../../../utils/hooks/bi/useTrackPreviewActions';
import { ClickOrigin } from '../../../../utils/hooks/bi/useTrackPreviewActions/useTrackPreviewActions.types';
import { useTrackUnsplashClick } from '../../../../utils/hooks/bi/useTrackUnsplashClick';
import { useGetItemPrice } from '../../../../utils/hooks/useGetItemPrice';
import {
  canDisplayCreatorPictureAndName,
  canOpenItemInImageEditor,
  getDurationInMMSS,
  isDocumentFile,
  isFile,
  isFolder,
  isMusicFile,
  isTransparentVideoFile,
} from '../../../../utils/media-manager-sdk/items';
import { isMediaFromWix, isUnsplash } from '../../../../utils/media-manager-sdk/sources';
import { isTransparentIconsCategory } from '../../../../utils/publicMedia';
import { DragSource, ExternalLink, ItemActionsRow, ResizedImage, Text, WixIcons } from '../../../common';
import { ActionButton } from '../../../common/ItemActionsRow/ItemActionsRow.types';
import { useVideoOnHoverControls } from '../../../common/Thumbnails/FileThumbnail/Thumbnails/VideoThumbnail/useVideoOnHoverControls';
import { GalleryFileControls } from '../../Gallery/GalleryFile/GalleryFileControls';
import { GenericItem } from '../../Generic/GenericItem';
import { IFile } from '../../types';
import baseItemStyles from '../SmartGridItemBase.scss';
import fileStyles from './SmartGridItem.scss';
import { SmartGridItemThumbnail } from './SmartGridItemThumbnail';
import { RelatedItemsButton } from './components/RelatedItemsButton';

const defaultActionButtons = [ActionButton.Preview, ActionButton.Purchase, ActionButton.AddToBoard];
const actionButtonsWithPSEntry = [
  ActionButton.CropAndEdit,
  ActionButton.Preview,
  ActionButton.Purchase,
  ActionButton.AddToBoard,
];

const SmartGridItemComponent: FC<IFile> = ({
  item,
  className,
  selected,
  isCut,
  onClick,
  onDoubleClick,
  onContextMenu,
  onRenameCancel,
  onRenameComplete,
  onNameChange,
  openExpandedView,
  beginDrag,
  canDrag,
  selectedItems,
  style,
  buyItem,
  isSquare,
  thumbMinWidth,
  publicMediaCurrentCategory,
  alwaysDisplayPrice,
  currentFolder,
  backgroundSize,
  isRelatedItem,
  onRelatedItemsButtonClick,
  renderTag,
  shouldShowRelatedItemsButton,
}) => {
  const { experiments } = useExperiments();
  const { getItemPrice } = useGetItemPrice();

  const price = getItemPrice(item);

  const duration = getDurationInMMSS(item);
  const isMediaFromWixSource = isMediaFromWix(item.source);
  const creatorLink = makeCreatorLink(item);
  const isAudioFile = isMusicFile(item);

  const thumbnailBackground =
    isMediaFromWixSource &&
    publicMediaCurrentCategory &&
    isTransparentIconsCategory(publicMediaCurrentCategory.parentId)
      ? ThumbnailBackground.LightBlue
      : ThumbnailBackground.Transparent;

  const canDisplayCreatorInfo = canDisplayCreatorPictureAndName(item);

  const canDisplayDuration = duration && !isAudioFile && !isTransparentVideoFile(item);
  const canDisplayPriceIcon = Boolean((isMediaFromWixSource || alwaysDisplayPrice) && price);
  const canDisplayName = item.name && !isUnsplash(item.source);

  const canDisplayRelatedItems =
    item.hasRelatedItems && !isRelatedItem && isMediaFromWixSource && shouldShowRelatedItemsButton;

  const withTransparentOverlay = isAudioFile || isDocumentFile(item);

  const history = useHistory();
  const { trackUnsplashLinkClickClicked } = useTrackUnsplashClick();
  const searchCreatorWorks = useCallback(
    (event) => {
      if (!isUnsplash(item.source)) {
        event.preventDefault();
        history.push(RouteBuilder.Search(item.source, `creator:"${item.creator}"`));
        return;
      }

      trackUnsplashLinkClickClicked(UnsplashTarget.DesignerName, UnsplashClickOrigin.UnsplashTab);
    },
    [item, trackUnsplashLinkClickClicked],
  );

  const {
    setVideoElement: videoNodeRef,
    setCanvasElement: canvasNodeRef,
    isHovered,
    isPlaying,
    setHoveredTrue: handleVideoNodeMouseEnter,
    handleVideoNodeMouseLeave,
  } = useVideoOnHoverControls();

  const itemStyle = useMemo(
    () => ({
      width: '100%',
      backgroundSize: isSquare || shouldSmartGridImageBeContained(item, thumbMinWidth) ? 'contain' : 'cover',
    }),
    [isSquare, item, thumbMinWidth],
  );

  const canDisplayEditUnsplashAction = canOpenItemInImageEditor(item);

  const actionButtons = canDisplayEditUnsplashAction ? actionButtonsWithPSEntry : defaultActionButtons;

  const { trackPreviewActionsExpandButtonClicked } = useTrackPreviewActions();
  const handleOpenExpandedViewClick = useCallback(() => {
    trackPreviewActionsExpandButtonClicked(ClickOrigin.Actions);
    openExpandedView();
  }, [trackPreviewActionsExpandButtonClicked, openExpandedView]);

  return (
    <GenericItem
      isRelated={isRelatedItem}
      renderTag={renderTag}
      className={classnames(
        baseItemStyles.item,
        fileStyles.item,
        {
          [fileStyles.selected]: selected,
          [baseItemStyles.selected]: selected,
          [fileStyles.isCut]: isCut,
          [fileStyles.square]: isSquare,
          [fileStyles.withTransparentOverlay]: withTransparentOverlay,
          [fileStyles.folderItem]: isFolder(item),
        },
        className,
      )}
      style={style}
      item={item}
      selected={selected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      onNameChange={onNameChange}
      onRenameComplete={onRenameComplete}
      onRenameCancel={onRenameCancel}
      dataHook="gallery-file"
      canDrag={canDrag}
      selectedItems={selectedItems}
    >
      {({ canDrag }) => (
        <DragSource item={item} beginDrag={beginDrag} type={DragType.File} disabled={!canDrag}>
          <div onMouseEnter={handleVideoNodeMouseEnter} onMouseLeave={handleVideoNodeMouseLeave}>
            <SmartGridItemThumbnail
              thumbnailBackground={thumbnailBackground}
              className={fileStyles.thumbnail}
              file={item}
              width={item.smartGridSize.width}
              height={item.smartGridSize.height}
              style={itemStyle}
              selected={selected}
              playVideoOnHover={isHovered}
              videoNodeRef={videoNodeRef}
              params={backgroundSize === 'cover' ? { mode: ResizeMode.Fill } : {}}
              isAlphaVideo={isTransparentVideoFile(item)}
              canvasNodeRef={canvasNodeRef}
              isPlaying={isPlaying}
            />

            {canDisplayDuration && (
              <div className={fileStyles.duration} data-hook="smart-grid-file-duration">
                <Text weight="normal" size="tiny" light>
                  {duration}
                </Text>
              </div>
            )}

            {canDisplayPriceIcon && (
              <div
                className={classnames(fileStyles.priceIcon, { [fileStyles.hiddenPrice]: isHovered })}
                data-hook="smart-grid-file-price-icon"
              >
                <WixIcons.PricingIcon />
              </div>
            )}

            <div className={fileStyles.borderOverlay} />

            {isFolder(item) && (
              <div className={fileStyles.folderOverlay}>
                <div className={fileStyles.folderDynamicOverlay}>
                  <ItemActionsRow
                    item={item}
                    buttons={actionButtons}
                    onOpenExpandedViewClick={handleOpenExpandedViewClick}
                    onBuyClick={buyItem}
                    currentFolder={currentFolder}
                    dataHook="items-actions-row"
                    actionsClickOrigin={ActionClickOrigin.File}
                  />
                </div>

                <Text
                  weight="bold"
                  size="small"
                  ellipsis
                  showDelay={TOOLTIP_SHOW_DELAY}
                  dataHook="smart-grid-file-name"
                  className={fileStyles.folderName}
                >
                  {
                    // @ts-expect-error
                    item.name
                  }
                </Text>
              </div>
            )}

            {isFile(item) && (
              <div className={fileStyles.overlay}>
                {isAudioFile && (
                  <div className={fileStyles.thumbnailControlsHolder}>
                    <GalleryFileControls file={item} onPlayClick={openExpandedView} />
                  </div>
                )}

                <ItemActionsRow
                  item={item}
                  buttons={actionButtons}
                  onOpenExpandedViewClick={handleOpenExpandedViewClick}
                  onBuyClick={buyItem}
                  currentFolder={currentFolder}
                  dataHook="items-actions-row"
                  actionsClickOrigin={ActionClickOrigin.File}
                />
                <div className={fileStyles.infoWrapper}>
                  <div className={fileStyles.info}>
                    {canDisplayCreatorInfo && (
                      <ExternalLink href={creatorLink}>
                        <ResizedImage
                          className={fileStyles.creatorPicture}
                          src={item.creatorPicture}
                          source={item.source}
                          width={36}
                          height={36}
                          dataHook="smart-grid-creator-picture"
                          onClick={searchCreatorWorks}
                        />
                      </ExternalLink>
                    )}

                    <div
                      className={classnames(fileStyles.creatorAndFileName, {
                        [fileStyles.withoutFileName]: !canDisplayName,
                      })}
                    >
                      {canDisplayName && (
                        <Text
                          weight="bold"
                          size="small"
                          light={!withTransparentOverlay}
                          ellipsis
                          showDelay={TOOLTIP_SHOW_DELAY}
                          dataHook="smart-grid-file-name"
                        >
                          {item.name}
                        </Text>
                      )}

                      {canDisplayCreatorInfo && (
                        <Text
                          weight={canDisplayName ? 'thin' : 'bold'}
                          size={canDisplayName ? 'tiny' : 'small'}
                          light
                          ellipsis
                          showDelay={TOOLTIP_SHOW_DELAY}
                          dataHook="smart-grid-creator"
                        >
                          <a
                            href={creatorLink}
                            target="_blank"
                            className={fileStyles.creatorLink}
                            onClick={searchCreatorWorks}
                            rel="noreferrer"
                          >
                            {item.creator}
                          </a>
                        </Text>
                      )}
                    </div>
                  </div>
                  {canDisplayRelatedItems && <RelatedItemsButton onClick={onRelatedItemsButtonClick} />}
                </div>
              </div>
            )}
          </div>
        </DragSource>
      )}
    </GenericItem>
  );
};

export const SmartGridItem: FC<IFile> = memo<IFile>(SmartGridItemComponent);
